// components/Manager/Order/Action/CombineOrdersDrawer.jsx
import { useMemo, useState } from "react"
import { Button, Descriptions, Divider, Drawer, message, Space, Typography, Alert } from "antd"
import { doc, runTransaction, deleteField } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { MergeCellsOutlined } from "@ant-design/icons"

const { Paragraph, Text } = Typography

/**
 * Combines selected orders by summing boxesCount and amountCreated into the original order.
 * - Keeper auto-picked: prefers split part 2 (original), else first selected.
 * - Clears split metadata on the keeper.
 * - Deletes all other selected orders.
 * - Does NOT modify labelCount.
 */

// --- split helpers ---
const isSplitPart = (o) =>
  !!o &&
  o.splitOrder === true &&
  o.totalParts === 2 &&
  (o.partOfSplit === 1 || o.partOfSplit === 2) &&
  !!o.otherOrderId &&
  !!o.splitRootId

const arePair = (a, b) =>
  isSplitPart(a) &&
  isSplitPart(b) &&
  a.id === b.otherOrderId &&
  b.id === a.otherOrderId &&
  a.splitRootId === b.splitRootId &&
  a.partOfSplit !== b.partOfSplit

export default function CombineOrdersDrawer({ selectedRows, onDone }) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const canOpen = selectedRows?.length === 2 && arePair(selectedRows[0], selectedRows[1])

  // Prefer the original doc (partOfSplit === 2). Fallback to first selected.
  const keeper = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return undefined
    const orig = selectedRows.find((r) => r?.splitOrder && r?.partOfSplit === 2)
    return orig || selectedRows[0]
  }, [selectedRows])

  const toDelete = useMemo(() => {
    if (!selectedRows || !keeper) return []
    return selectedRows.filter((r) => r.id !== keeper.id)
  }, [selectedRows, keeper])

  // UI preview — sums from what's selected
  const previewBoxes = useMemo(
    () => (selectedRows || []).reduce((s, r) => s + Number(r?.boxesCount || 0), 0),
    [selectedRows]
  )
  const previewAmount = useMemo(
    () => (selectedRows || []).reduce((s, r) => s + Number(r?.amountCreated || 0), 0),
    [selectedRows]
  )

  const handleOpen = () => {
    if (!(selectedRows?.length === 2 && arePair(selectedRows[0], selectedRows[1]))) {
      message.warning("Select the two parts of the SAME split order to combine.")
      return
    }
    setOpen(true)
  }
  const handleClose = () => setOpen(false)

  const handleCombine = async () => {
    if (!(selectedRows?.length === 2 && arePair(selectedRows[0], selectedRows[1]))) {
      message.warning("Select the two parts of the SAME split order to combine.")
      return
    }

    setSubmitting(true)
    try {
      const [selA, selB] = selectedRows
      const ids = [selA.id, selB.id]

      await runTransaction(db, async (tx) => {
        const snaps = await Promise.all(ids.map((id) => tx.get(doc(db, "orders", id))))
        const docs = snaps.map((s) => ({ id: s.id, data: s.data() || {} }))
        if (docs.length !== 2) throw new Error("Failed to read both orders.")

        const A = { id: docs[0].id, ...docs[0].data }
        const B = { id: docs[1].id, ...docs[1].data }
        if (!arePair(A, B)) throw new Error("Selected orders are not a matching split pair.")

        const freshKeeper = A.partOfSplit === 2 ? A : B.partOfSplit === 2 ? B : A
        const freshOther = freshKeeper.id === A.id ? B : A

        const sumBoxes = Number(A.boxesCount ?? 0) + Number(B.boxesCount ?? 0)
        const sumAmount = Number(A.amountCreated ?? 0) + Number(B.amountCreated ?? 0)
        if (!Number.isFinite(sumBoxes) || !Number.isFinite(sumAmount)) {
          throw new Error("Invalid boxesCount/amountCreated on selected orders.")
        }

        const keeperRef = doc(db, "orders", freshKeeper.id)
        tx.update(keeperRef, {
          boxesCount: sumBoxes,
          amountCreated: sumAmount,
          splitOrder: deleteField(),
          partOfSplit: deleteField(),
          totalParts: deleteField(),
          otherOrderId: deleteField(),
          beforeSplitBoxesCount: deleteField(),
          beforeSplitAmountCreated: deleteField(),
          splitTime: deleteField(),
          splitRootId: deleteField(),
          splitParentId: deleteField(),
          managerSplitComment: deleteField(),
          machineOrderSplitComment: deleteField(),
        })

        tx.delete(doc(db, "orders", freshOther.id))
      })

      message.success("Combined back into the original order. Other part removed.")
      handleClose()
      onDone?.()
    } catch (e) {
      console.error(e)
      message.error(e?.message || "Failed to combine. See console for details.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button type="default" disabled={!canOpen} icon={<MergeCellsOutlined />} onClick={handleOpen}>
        Combine Selected
      </Button>

      <Drawer
        title="Combine selected orders"
        width={640}
        open={open}
        onClose={handleClose}
        extra={
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleCombine}
              loading={submitting}
              disabled={!keeper || toDelete.length < 1}>
              Confirm Combine
            </Button>
          </Space>
        }>
        <Alert
          type="warning"
          showIcon
          message="Keeps the original order’s ID (prefers part 2), sums boxesCount and amountCreated into it, clears split flags, and DELETES the other selected orders."
          style={{ marginBottom: 12 }}
        />

        <Divider>Keeper (auto-picked)</Divider>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Keeper">
            {keeper
              ? `${keeper.orderNumberPrefix || ""}${keeper.justOrderNumber || keeper.id}`
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="New boxesCount (sum)">
            <b>{previewBoxes}</b>
          </Descriptions.Item>
          <Descriptions.Item label="New amountCreated (sum)">
            <b>{previewAmount}</b>
          </Descriptions.Item>
          <Descriptions.Item label="Docs to delete">{toDelete.length}</Descriptions.Item>
        </Descriptions>

        <Divider>Selected orders</Divider>
        <Space direction="vertical" style={{ width: "100%" }}>
          {(selectedRows || []).map((r) => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between" }}>
              <span>
                {r.orderNumberPrefix || ""}
                {r.justOrderNumber || r.id}
                {r?.splitOrder ? (
                  <em style={{ marginLeft: 6, opacity: 0.7 }}>
                    {`(part ${r.partOfSplit || "?"}/${r.totalParts || (r.partOfSplit ? 2 : "?")})`}
                  </em>
                ) : null}
              </span>
              <span>
                boxesCount: <b>{r.boxesCount ?? 0}</b> &nbsp;|&nbsp; amountCreated:{" "}
                <b>{r.amountCreated ?? 0}</b>
              </span>
            </div>
          ))}
        </Space>

        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          <Text>
            The keeper becomes a normal order again (split flags removed). Other selected orders are
            deleted. <i>labelCount is not changed.</i>
          </Text>
        </Paragraph>
      </Drawer>
    </>
  )
}
